import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import AWSSetup from './components/AWSSetup';
import DashboardPage from './pages/DashboardPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://spectraine-backend.onrender.com';

// ── Register Form ──────────────────────────────────────────────
function RegisterForm({ onSuccess, onSwitchToLogin }) {
  const [form, setForm] = useState({ email: '', company: '', full_name: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('tos', tosAccepted ? 'true' : 'false');
      const res = await fetch(`${BACKEND_URL}/register`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registration failed');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="sp-form">
      {error && <div className="sp-error">{error}</div>}

      <input
        className="sp-input"
        placeholder="Full name"
        value={form.full_name}
        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
        required
      />
      <input
        className="sp-input"
        placeholder="Company name"
        value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })}
        required
      />
      <input
        className="sp-input"
        placeholder="Work email"
        type="email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        required
      />
      <input
        className="sp-input"
        placeholder="Password (min 8 chars)"
        type="password"
        value={form.password}
        onChange={(e) => setForm({ ...form, password: e.target.value })}
        required
        minLength={8}
      />

      <div className="sp-tos-row">
        <input
          type="checkbox"
          id="tos"
          checked={tosAccepted}
          onChange={(e) => setTosAccepted(e.target.checked)}
          required
        />
        <label htmlFor="tos" className="sp-tos-label">
          I agree to the{' '}
          <a href="/#/terms" target="_blank" rel="noreferrer" className="sp-link">Terms of Service</a>
          {' '}and{' '}
          <a href="/#/privacy" target="_blank" rel="noreferrer" className="sp-link">Privacy Policy</a>
        </label>
      </div>

      <button type="submit" disabled={loading} className="sp-btn-primary sp-btn-full">
        {loading ? 'Creating account…' : 'Create Account →'}
      </button>

      <p className="sp-switch-line">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="sp-link-btn">
          Log in
        </button>
      </p>
    </form>
  );
}

// ── Landing Page ───────────────────────────────────────────────
function LandingPage() {
  const [view, setView] = useState('home');
  const navigate = useNavigate();

  if (view === 'register') {
    return (
      <div className="sp-auth-screen">
        <div className="sp-auth-card">
          <div className="sp-auth-logo">
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" aria-label="Spectraine">
              <path d="M16 3L4 9v7c0 6.627 5.148 12.347 12 13.9C22.852 28.347 28 22.627 28 16V9L16 3z" stroke="url(#sg)" strokeWidth="2" fill="none"/>
              <rect x="10" y="14" width="2" height="8" rx="1" fill="url(#sg)"/>
              <rect x="15" y="11" width="2" height="11" rx="1" fill="url(#sg)"/>
              <rect x="20" y="16" width="2" height="6" rx="1" fill="url(#sg)"/>
              <defs>
                <linearGradient id="sg" x1="4" y1="3" x2="28" y2="29" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#22d3ee"/>
                  <stop offset="1" stopColor="#818cf8"/>
                </linearGradient>
              </defs>
            </svg>
            <span className="sp-wordmark">SPECTRAINE</span>
          </div>
          <h2 className="sp-auth-heading">Create your account</h2>
          <p className="sp-auth-sub">Start your Spectraine setup — subscription via Stripe after sign up.</p>
          <RegisterForm onSuccess={() => navigate('/dashboard')} onSwitchToLogin={() => setView('login')} />
        </div>
      </div>
    );
  }

  if (view === 'login') {
    return <LoginPage onSwitchToRegister={() => setView('register')} />;
  }

  return (
    <div className="sp-landing">

      {/* Nav */}
      <nav className="sp-nav">
        <div className="sp-nav-logo">
          <svg width="24" height="24" viewBox="0 0 32 32" fill="none" aria-label="Spectraine">
            <path d="M16 3L4 9v7c0 6.627 5.148 12.347 12 13.9C22.852 28.347 28 22.627 28 16V9L16 3z" stroke="url(#sn)" strokeWidth="2" fill="none"/>
            <rect x="10" y="14" width="2" height="8" rx="1" fill="url(#sn)"/>
            <rect x="15" y="11" width="2" height="11" rx="1" fill="url(#sn)"/>
            <rect x="20" y="16" width="2" height="6" rx="1" fill="url(#sn)"/>
            <defs>
              <linearGradient id="sn" x1="4" y1="3" x2="28" y2="29" gradientUnits="userSpaceOnUse">
                <stop stopColor="#22d3ee"/>
                <stop offset="1" stopColor="#818cf8"/>
              </linearGradient>
            </defs>
          </svg>
          <span className="sp-wordmark">SPECTRAINE</span>
        </div>
        <div className="sp-nav-actions">
          <button onClick={() => setView('login')} className="sp-btn-ghost">Log in</button>
          <button onClick={() => setView('register')} className="sp-btn-primary">Get Started</button>
        </div>
      </nav>

      {/* Hero */}
      <section className="sp-hero">
        <div className="sp-hero-inner">
          <span className="sp-badge">AWS Security &amp; Cost Intelligence</span>
          <h1 className="sp-hero-heading">
            Find security holes.<br />
            <span className="sp-gradient-text">Cut your AWS bill.</span>
          </h1>
          <p className="sp-hero-sub">
            Spectraine scans your entire AWS environment daily — finding critical security risks
            and cost waste that most teams never see. Connected in 2 minutes via CloudFormation.
          </p>
          <div className="sp-hero-ctas">
            <button onClick={() => setView('register')} className="sp-btn-primary sp-btn-lg">
              Start Securing Your AWS →
            </button>
            <Link to="/pricing" className="sp-btn-outline sp-btn-lg">
              See Pricing
            </Link>
          </div>
          <p className="sp-hero-footnote">No contracts · Cancel anytime · $497/month</p>
        </div>
      </section>

      {/* Features */}
      <section className="sp-features">
        <div className="sp-features-grid">
          <div className="sp-feature-wide">
            <p className="sp-feature-eyebrow">Security Posture</p>
            <h3 className="sp-feature-heading">Daily scans across your entire AWS estate</h3>
            <p className="sp-feature-body">
              S3 public exposure, overpermissive IAM roles, open Security Groups, unencrypted RDS
              instances — Spectraine checks all of it across every region, every day. Leaders see
              a compliance score; engineers get the exact resource ARN and fix steps.
            </p>
            <div className="sp-score-table">
              <div className="sp-score-row"><span>IAM least-privilege</span><span className="sp-chip sp-chip-warn">12 issues</span></div>
              <div className="sp-score-row"><span>S3 public access</span><span className="sp-chip sp-chip-ok">Clean</span></div>
              <div className="sp-score-row"><span>Security Groups</span><span className="sp-chip sp-chip-crit">3 critical</span></div>
              <div className="sp-score-row"><span>RDS encryption</span><span className="sp-chip sp-chip-ok">Clean</span></div>
            </div>
          </div>

          <div className="sp-feature-card">
            <p className="sp-feature-eyebrow">Cost Intelligence</p>
            <h3 className="sp-feature-heading">Average 35% AWS bill reduction</h3>
            <p className="sp-feature-body">
              Overprovisioned <code>m5.2xlarge</code> instances, forgotten EBS volumes, and
              Savings Plan gaps. Leaders see the dollar save; engineers see the exact resource.
            </p>
          </div>

          <div className="sp-feature-card">
            <p className="sp-feature-eyebrow">Reports &amp; Alerts</p>
            <h3 className="sp-feature-heading">Branded PDFs and instant Slack alerts</h3>
            <p className="sp-feature-body">
              Executive-ready reports with ROI and breach liability estimates. Critical findings
              hit your Slack channel the moment they are detected.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="sp-footer">
        <div className="sp-footer-logo">
          <svg width="18" height="18" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path d="M16 3L4 9v7c0 6.627 5.148 12.347 12 13.9C22.852 28.347 28 22.627 28 16V9L16 3z" stroke="url(#sf)" strokeWidth="2" fill="none"/>
            <rect x="10" y="14" width="2" height="8" rx="1" fill="url(#sf)"/>
            <rect x="15" y="11" width="2" height="11" rx="1" fill="url(#sf)"/>
            <rect x="20" y="16" width="2" height="6" rx="1" fill="url(#sf)"/>
            <defs>
              <linearGradient id="sf" x1="4" y1="3" x2="28" y2="29" gradientUnits="userSpaceOnUse">
                <stop stopColor="#22d3ee"/>
                <stop offset="1" stopColor="#818cf8"/>
              </linearGradient>
            </defs>
          </svg>
          <span className="sp-wordmark">SPECTRAINE</span>
        </div>
        <div className="sp-footer-links">
          <Link to="/pricing" className="sp-footer-link">Pricing</Link>
          <Link to="/privacy" className="sp-footer-link">Privacy</Link>
          <Link to="/terms" className="sp-footer-link">Terms</Link>
          <a href="mailto:andrei@spectraine.com" className="sp-footer-link">Contact</a>
        </div>
        <span className="sp-footer-copy">© 2026 Spectraine Ltd</span>
      </footer>
    </div>
  );
}

// ── Success Page ───────────────────────────────────────────────
function SuccessPage() {
  const navigate = useNavigate();
  return (
    <div className="sp-auth-screen">
      <div className="sp-success-card">
        <div className="sp-success-icon">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="23" stroke="url(#sc)" strokeWidth="2"/>
            <path d="M14 24l7 7 13-13" stroke="url(#sc)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <defs>
              <linearGradient id="sc" x1="1" y1="1" x2="47" y2="47" gradientUnits="userSpaceOnUse">
                <stop stopColor="#22d3ee"/>
                <stop offset="1" stopColor="#818cf8"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h1 className="sp-success-heading">You&apos;re in!</h1>
        <p className="sp-success-body">
          Your Spectraine Professional subscription is now active. Connect your AWS account
          to get your first security and cost report.
        </p>
        <button onClick={() => navigate('/dashboard')} className="sp-btn-primary sp-btn-lg">
          Go to Dashboard →
        </button>
      </div>
    </div>
  );
}

// ── App Router ─────────────────────────────────────────────────
export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/aws-setup" element={<AWSSetup />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Routes>
    </Router>
  );
}